import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import { normalizeUser } from '../utils/userUtils';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in - check localStorage first (persistent)
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    
    if (storedUser && token) {
      setUser(normalizeUser(JSON.parse(storedUser)));
      authAPI.getCurrentUser().then((userData) => {
        if (userData) {
          setUser(normalizeUser(userData));
        } else {
          setUser(null);
        }
        setLoading(false);
      }).catch(() => {
        setUser(null);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    try {
      const data = await authAPI.login(credentials);
      setUser(normalizeUser(data.user));
      return data;
    } catch (error) {
      throw error;
    }
  };

  const googleLogin = async (credential) => {
    try {
      const data = await authAPI.googleLogin(credential);
      setUser(normalizeUser(data.user));
      return data;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const data = await authAPI.register(userData);
      // Auto-login after registration - check for tokens.access or token
      if (data.tokens?.access || data.token) {
        setUser(normalizeUser(data.user));
      }
      return data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
    // Cart persists in localStorage even after logout
  };

  const updateProfile = async (userData) => {
    try {
      const updatedUser = await authAPI.updateProfile(userData);
      setUser(normalizeUser(updatedUser));
      return normalizeUser(updatedUser);
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    googleLogin,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
